import React, { createContext, useState, useCallback, useMemo, Dispatch, SetStateAction } from 'react'
import type { DistributorSortOptions } from '../../../../graphql/api/types'

export type DistributorTabs = 'withDistributors' | 'withoutDistributors'

type HeaderData = {
  sort: DistributorSortOptions
  search: string
}

type HeaderSetters = {
  setSort: (options: DistributorSortOptions) => void
  setSearch: (search: string) => void
}

type Header = {
  data: HeaderData
  setters: HeaderSetters
}

type ListDistributorsErrorState = {
  value: Error | null
  refetch: () => Promise<unknown>
}

type ListDistributorsContextType = {
  header: Header
  error: ListDistributorsErrorState & {
    set: (errorState: ListDistributorsErrorState) => void
  }
  setDistributorFormState: Dispatch<SetStateAction<'edit' | 'create'>>
  distributorFormState: 'edit' | 'create'
  setSelectedDistributorId: Dispatch<SetStateAction<string>>
  selectedDistributorId: string
  setSelectedTab: Dispatch<SetStateAction<DistributorTabs>>
  selectedTab: DistributorTabs
}

const INITIAL_DATA: ListDistributorsContextType = {
  header: {
    data: {
      search: '',
      sort: (localStorage.getItem('DISTRIBUTOR_PREFERRED_SORT_OPTION') as DistributorSortOptions) || 'MOST_RECENT',
    },
    setters: {
      setSort: () => undefined,
      setSearch: () => undefined,
    },
  },
  error: {
    value: null,
    refetch: async () => undefined,
    set: () => undefined,
  },
  setDistributorFormState: () => undefined,
  distributorFormState: 'create',
  setSelectedDistributorId: () => undefined,
  selectedDistributorId: '',
  setSelectedTab: () => undefined,
  selectedTab: 'withDistributors',
}

export const ListDistributorsContext = createContext<ListDistributorsContextType>(INITIAL_DATA)

export const ListDistributorsProvider: React.FC = ({ children }) => {
  const [distributorFormState, setDistributorFormState] = useState<'create' | 'edit'>('edit')
  const [selectedDistributorId, setSelectedDistributorId] = useState('')
  const [selectedTab, setSelectedTab] = useState<DistributorTabs>('withoutDistributors')
  const [headerData, setHeaderData] = useState<HeaderData>(INITIAL_DATA.header.data)
  const [errorState, setErrorState] = useState<ListDistributorsErrorState>({
    value: null,
    refetch: async () => undefined,
  })

  const setSort = useCallback((option: DistributorSortOptions): void => {
    setHeaderData((prev) => ({ ...prev, sort: option }))
  }, [])

  const setSearch = useCallback(
    (search: string): void => {
      if (search !== headerData.search) {
        setHeaderData((prev) => ({ ...prev, search }))
      }
    },
    [headerData.search],
  )

  const setContextError = useCallback((error: ListDistributorsErrorState) => {
    setErrorState(error)
  }, [])

  const contextValue = useMemo(
    () => ({
      header: {
        data: headerData,
        setters: {
          setSort,
          setSearch,
        },
      },
      error: {
        value: errorState.value,
        refetch: errorState.refetch,
        set: setContextError,
      },
      setDistributorFormState: setDistributorFormState,
      distributorFormState: distributorFormState,
      setSelectedDistributorId: setSelectedDistributorId,
      selectedDistributorId: selectedDistributorId,
      setSelectedTab: setSelectedTab,
      selectedTab: selectedTab,
    }),
    [
      headerData,
      setSort,
      setSearch,
      errorState,
      setContextError,
      setSelectedDistributorId,
      selectedDistributorId,
      distributorFormState,
      setDistributorFormState,
      setSelectedTab,
      selectedTab,
    ],
  )

  return <ListDistributorsContext.Provider value={contextValue}>{children}</ListDistributorsContext.Provider>
}
